import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";

export const fetchSchedulingProfile = createAsyncThunk(
  "clientBookingSettings/fetchSchedulingProfile",
  async (_, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/profile/`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch scheduling profile"
      );
    }
  }
);

export const enableSchedulingService = createAsyncThunk(
  "clientBookingSettings/enableSchedulingService",
  async (_, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/service/`,
        { method: "POST" }
      );
      return data;
    } catch (error) {
      if (error?.status === 403) {
        return rejectWithValue({ subscriptionRequired: true });
      }
      return rejectWithValue(
        error.message ||
          error.error ||
          "Failed to enable scheduling service"
      );
    }
  }
);

export const disableSchedulingService = createAsyncThunk(
  "clientBookingSettings/disableSchedulingService",
  async (_, { rejectWithValue }) => {
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/scheduling/service/`,
        { method: "DELETE" }
      );
      return true;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to disable scheduling service"
      );
    }
  }
);

export const updateSchedulingProfile = createAsyncThunk(
  "clientBookingSettings/updateSchedulingProfile",
  async ({ body, requiresReEnable }, { rejectWithValue }) => {
    try {
      if (requiresReEnable) {
        // Disable -> Update -> Re-enable
        await request(
          `${process.env.REACT_APP_API_URL}/scheduling/service/`,
          { method: "DELETE" }
        );
      }

      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/profile/`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        }
      );

      if (requiresReEnable) {
        try {
          await request(
            `${process.env.REACT_APP_API_URL}/scheduling/service/`,
            { method: "POST" }
          );
        } catch (reEnableError) {
          // Profile was updated but re-enable failed.
          // Return the updated profile so state stays consistent,
          // but signal the partial failure so the UI can inform the user.
          return rejectWithValue({
            partialSuccess: true,
            message:
              "Settings saved, but the service could not be re-enabled. " +
              "Please turn it on manually.",
          });
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update scheduling profile"
      );
    }
  }
);

export const fetchPreviewConfig = createAsyncThunk(
  "clientBookingSettings/fetchPreviewConfig",
  async (
    { durationMinutes, minBufferTimeMinutes, maxDaysInFuture, weeklyHours },
    { rejectWithValue }
  ) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/profile/preview/`,
        {
          method: "POST",
          body: JSON.stringify({
            duration_minutes: durationMinutes,
            min_buffer_time_minutes: minBufferTimeMinutes,
            max_days_in_future: maxDaysInFuture,
            weekly_hours: weeklyHours,
          }),
        }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || error.error || "Failed to fetch preview"
      );
    }
  }
);

const initialState = {
  profile: null,
  fetchStatus: "idle",
  fetchError: null,
  enableStatus: "idle",
  enableError: null,
  disableStatus: "idle",
  disableError: null,
  previewData: null,
  previewStatus: "idle",
  previewError: null,
  updateStatus: "idle",
  updateError: null,
};

const clientBookingSettingsSlice = createSlice({
  name: "clientBookingSettings",
  initialState,
  reducers: {
    clearEnableStatus: (state) => {
      state.enableStatus = "idle";
      state.enableError = null;
    },
    clearDisableStatus: (state) => {
      state.disableStatus = "idle";
      state.disableError = null;
    },
    clearPreviewData: (state) => {
      state.previewData = null;
      state.previewStatus = "idle";
      state.previewError = null;
    },
    clearUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch scheduling profile
      .addCase(fetchSchedulingProfile.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchError = null;
      })
      .addCase(fetchSchedulingProfile.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        state.profile = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchSchedulingProfile.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchError = action.payload;
      })
      // Enable scheduling service
      .addCase(enableSchedulingService.pending, (state) => {
        state.enableStatus = "loading";
        state.enableError = null;
      })
      .addCase(enableSchedulingService.fulfilled, (state) => {
        state.enableStatus = "success";
        state.enableError = null;
      })
      .addCase(enableSchedulingService.rejected, (state, action) => {
        state.enableStatus = "failed";
        state.enableError = action.payload;
      })
      // Disable scheduling service
      .addCase(disableSchedulingService.pending, (state) => {
        state.disableStatus = "loading";
        state.disableError = null;
      })
      .addCase(disableSchedulingService.fulfilled, (state) => {
        state.disableStatus = "success";
        state.disableError = null;
      })
      .addCase(disableSchedulingService.rejected, (state, action) => {
        state.disableStatus = "failed";
        state.disableError = action.payload;
      })
      // Fetch preview config
      .addCase(fetchPreviewConfig.pending, (state) => {
        state.previewStatus = "loading";
        state.previewError = null;
      })
      .addCase(fetchPreviewConfig.fulfilled, (state, action) => {
        state.previewStatus = "success";
        state.previewData = action.payload;
        state.previewError = null;
      })
      .addCase(fetchPreviewConfig.rejected, (state, action) => {
        state.previewStatus = "failed";
        state.previewError = action.payload;
      })
      // Update scheduling profile
      .addCase(updateSchedulingProfile.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateSchedulingProfile.fulfilled, (state, action) => {
        state.updateStatus = "success";
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updateSchedulingProfile.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      });
  },
});

export const {
  clearEnableStatus,
  clearDisableStatus,
  clearPreviewData,
  clearUpdateStatus,
} = clientBookingSettingsSlice.actions;
export default clientBookingSettingsSlice.reducer;
